"use client";

import React, { useMemo } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const PRACTICE = {
  name: "Alpha Dental – Universal City",
  url: "https://ddsalpha.com/",
  telephone: "+12107755177",
  phoneDisplay: "(210) 775-5177",
  address: {
    street: "916 Coronado Blvd",
    city: "Universal City",
    state: "TX",
    zip: "78148",
    country: "US"
  },
  nearby: "Randolph Air Force Base",
  geo: {
    latitude: 29.5539,
    longitude: -98.2956
  },
  areaServed: ["Universal City, TX", "Live Oak, TX", "Converse, TX", "San Antonio, TX", "Randolph Air Force Base"]
};
const DR_GAYA = "https://ddsalpha.com/wp-content/uploads/2026/05/Dr-Gaya-1.webp";
const DR_TAYLOR = "https://ddsalpha.com/wp-content/uploads/2026/05/Dr-Taylor-1.webp";
const DR_JOHN = "https://ddsalpha.com/wp-content/uploads/2026/05/Dr-Johnn.webp";
const DR_NABEEL = "https://ddsalpha.com/wp-content/uploads/2026/05/Dr.-Aeliya-Nabeel.webp";
type DentistCard = {
  name: string;
  titleLine1: string;
  titleLine2: string;
  img: string;
  alt: string;
  highlight?: boolean;
};
const DENTISTS: DentistCard[] = [{
  name: "Dr. Gayatri Pradhan, DMD",
  titleLine1: "Owner Dentist",
  titleLine2: "Family & General Dentistry",
  img: DR_GAYA,
  alt: "Dr. Gayatri Pradhan, DMD – Owner Dentist in Universal City TX",
  highlight: true
}, {
  name: "Dr. Aeliya Nabeel",
  titleLine1: "Associate Dentist",
  titleLine2: "General Dentistry & Emergency Care",
  img: DR_NABEEL,
  alt: "Dr. Rodriguez, DDS – Emergency Dentist and General Dentist in Universal City TX",
  highlight: true
}, {
  name: "Dr. Rodriguez, DDS",
  titleLine1: "Associate Dentist",
  titleLine2: "General Dentistry & Emergency Care",
  img: DR_JOHN,
  alt: "Dr. Rodriguez, DDS – Emergency Dentist and General Dentist in Universal City TX",
  highlight: true
}, {
  name: "Dr. Taylor, DMD",
  titleLine1: "Associate Dentist",
  titleLine2: "Restorative & Cosmetic Dentistry",
  img: DR_TAYLOR,
  alt: "Dr. Taylor, DMD – Restorative and Cosmetic Dentist in Universal City TX",
  highlight: true
}];
export default function AboutAlphaDental() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const jsonLd = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "Dentist",
      name: PRACTICE.name,
      url: PRACTICE.url,
      telephone: PRACTICE.telephone,
      address: {
        "@type": "PostalAddress",
        streetAddress: PRACTICE.address.street,
        addressLocality: PRACTICE.address.city,
        addressRegion: PRACTICE.address.state,
        postalCode: PRACTICE.address.zip,
        addressCountry: PRACTICE.address.country
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: PRACTICE.geo.latitude,
        longitude: PRACTICE.geo.longitude
      },
      areaServed: PRACTICE.areaServed,
      medicalSpecialty: ["General Dentistry", "Family Dentistry", "Cosmetic Dentistry", "Emergency Dentistry", "Dental Implants"]
    };
  }, []);
  return <section ref={ref} className="bg-[#F7DDE2] py-16 md:py-20 px-4" aria-label="About Alpha Dental in Universal City, Texas">
      {}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      <div className="max-w-7xl mx-auto">
        {}
        <div className={`max-w-3xl mx-auto transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100" : "opacity-0 translate-y-0"}`}>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-[#3F2E32]">
            <strong>Trusted Family &amp; General Dentists</strong> in{" "}
            <strong>Universal City, TX</strong>
          </h2>

          {}
          <div className="mt-4 text-sm md:text-base text-[#5A4449]">
            <address className="not-italic leading-relaxed">
              <strong>{PRACTICE.name}</strong> ·{" "}
              <strong>{PRACTICE.address.street}</strong>,{" "}
              <strong>
                {PRACTICE.address.city}, {PRACTICE.address.state}{" "}
                {PRACTICE.address.zip}
              </strong>{" "}
              · Near <strong>{PRACTICE.nearby}</strong> ·{" "}
              <Link to={`tel:${PRACTICE.telephone}`} className="font-semibold text-[#A05D63] hover:text-[#8E4F55] underline underline-offset-4">
                {PRACTICE.phoneDisplay}
              </Link>
            </address>
          </div>

          <div className="mt-6 space-y-4 text-base md:text-lg text-[#5A4449]">
            <p>
              At <strong>{PRACTICE.name}</strong>, our dentists provide{" "}
              <strong>affordable, comprehensive dental care</strong> for{" "}
              <strong>children, teens, adults, and seniors</strong>. Conveniently
              located in <strong>Universal City, TX</strong> near{" "}
              <strong>{PRACTICE.nearby}</strong>, we welcome new patients and focus
              on <strong>comfortable visits</strong>,{" "}
              <strong>clear communication</strong>, and{" "}
              <strong>modern dental treatment options</strong>.
            </p>

            <p>
              Our services include <strong>preventive cleanings and exams</strong>,{" "}
              <strong>tooth-colored fillings</strong>, <strong>crowns</strong>,{" "}
              <strong>cosmetic dentistry</strong>,{" "}
              <strong>emergency dental care</strong>, and{" "}
              <strong>dental implants</strong>. Whether you're here for a routine
              checkup or help with <strong>tooth pain</strong>, our team is here to
              keep your smile <strong>healthy</strong> and{" "}
              <strong>confident</strong>.
            </p>

            <p>
              Families across <strong>Universal City</strong>,{" "}
              <strong>Live Oak</strong>, <strong>Converse</strong>, and{" "}
              <strong>San Antonio</strong> trust Alpha Dental for{" "}
              <strong>friendly service</strong>,{" "}
              <strong>advanced dental technology</strong>, and{" "}
              <strong>treatment plans</strong> tailored to long-term oral health
              goals.
            </p>
          </div>
        </div>

        {}
        <div className="mt-14">
          <h3 className="text-2xl md:text-3xl font-display font-bold text-[#3F2E32] text-center">
            Meet Our Dentists
          </h3>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8">
            {DENTISTS.map(d => <Card key={d.name} className={["bg-[#F4C9D3] shadow-soft hover:shadow-medium transition-all ring-2 ring-[#C27A7E]", d.highlight ? "ring-2 ring-[#C27A7E]" : ""].join(" ")}>
                <div className="p-6">
                  {}
                  <div className={["mx-auto w-[160px] sm:w-[176px]", "aspect-[3/4] overflow-hidden", "rounded-2xl bg-white/40", d.highlight ? "border-4 border-[#D6BBA3]" : "border-2 border-[#D6BBA3]", "shadow-soft"].join(" ")} aria-hidden="true">
                    <img src={d.img} alt={d.alt} width="160" height="213" loading="lazy" decoding="async" fetchpriority="high" className="h-full w-full object-cover aspect-[3/4]" />
                  </div>

                  <h4 className="mt-5 text-lg sm:text-xl font-display font-bold text-center text-[#3F2E32]">
                    {d.name}
                  </h4>

                  <p className="mt-1 text-sm text-center text-[#5A4449] leading-snug">
                    <strong>{d.titleLine1}</strong> · <strong>{d.titleLine2}</strong>
                  </p>
                </div>
              </Card>)}
          </div>

          {}
          <p className="mt-8 text-center text-sm text-[#5A4449]">
            Looking for a <strong>family dentist</strong>,{" "}
            <strong>general dentist</strong>, or{" "}
            <strong>emergency dentist near Randolph Air Force Base</strong>? Visit{" "}
            <strong>{PRACTICE.name}</strong> at{" "}
            <strong>
              {PRACTICE.address.street}, {PRACTICE.address.city},{" "}
              {PRACTICE.address.state} {PRACTICE.address.zip}
            </strong>
            .
          </p>
        </div>
      </div>
    </section>;
}